import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Check, Loader2, ShoppingBag, AlertTriangle, Package, Truck, Clock, ArrowRight } from 'lucide-react';
import { useCart } from '../context/CartContext';
import SEO from '../utils/seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const { fetchCart } = useCart();
  const [status, setStatus] = useState('loading');
  const [orderData, setOrderData] = useState(null);

  const method = searchParams.get('method');
  const orderId = searchParams.get('order_id');

  useEffect(() => {
    SEO.applyNoIndex('Order Confirmed | PosterFire', '/checkout/success');
    return () => SEO.resetDefaults();
  }, []);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        if (method === 'square' && orderId) {
          const res = await axios.get(`${API}/square/payment-status/${orderId}`);
          setOrderData(res.data);
          setStatus(res.data.payment_status === 'paid' ? 'success' : res.data.payment_status === 'pending' || res.data.payment_status === 'processing' ? 'pending' : 'failed');
          fetchCart && fetchCart();
          return;
        }
        setStatus('failed');
      } catch {
        setStatus('failed');
      }
    };
    checkStatus();
  }, [method, orderId, fetchCart]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" data-testid="checkout-loading">
        <div className="text-center">
          <div className="relative w-20 h-20 mx-auto mb-6">
            <div className="absolute inset-0 rounded-full border-4 border-gray-200" />
            <div className="absolute inset-0 rounded-full border-4 border-orange-500 border-t-transparent animate-spin" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Confirming your payment...</h1>
          <p className="text-sm text-gray-500">This will only take a moment.</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    const shortId = (orderData?.order_id || orderId || '').slice(-8).toUpperCase();
    return (
      <div className="min-h-screen bg-white" data-testid="order-success">
        {/* Confetti bar */}
        <div className="h-1.5 bg-gradient-to-r from-green-400 via-emerald-500 to-teal-500" />

        <div className="max-w-2xl mx-auto px-4 py-12 sm:py-20">
          {/* Success animation */}
          <div className="text-center mb-10">
            <div className="relative w-24 h-24 mx-auto mb-8">
              <div className="absolute inset-0 bg-green-100 rounded-full animate-ping opacity-20" />
              <div className="relative w-24 h-24 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center shadow-lg shadow-green-500/30">
                <Check className="w-12 h-12 text-white" strokeWidth={3} />
              </div>
            </div>
            <h1 className="text-3xl sm:text-4xl font-black text-gray-900 tracking-tight mb-3">
              Order Confirmed!
            </h1>
            <p className="text-lg text-gray-500 max-w-md mx-auto">
              Thank you for shopping with PosterFire. We're getting your posters ready!
            </p>
          </div>

          {/* Order ID Card */}
          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 mb-8" data-testid="order-details-card">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Order Number</span>
              <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full">Confirmed</span>
            </div>
            <p className="text-2xl font-mono font-black text-gray-900 tracking-widest mb-4" data-testid="order-id">
              #{shortId}
            </p>
            {orderData?.total && (
              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <span className="text-sm text-gray-500">Total Charged</span>
                <span className="text-xl font-bold text-gray-900">${orderData.total.toFixed(2)}</span>
              </div>
            )}
          </div>

          {/* Timeline */}
          <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-8" data-testid="shipping-timeline">
            <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-6">What Happens Next</h2>
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                    <Check className="w-5 h-5 text-white" />
                  </div>
                  <div className="w-0.5 h-full bg-gray-200 mt-2" />
                </div>
                <div className="pb-6">
                  <p className="font-semibold text-gray-900 text-sm">Payment Received</p>
                  <p className="text-xs text-gray-500 mt-1">Your order is confirmed and being prepared</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 bg-orange-100 border-2 border-orange-400 rounded-full flex items-center justify-center">
                    <Package className="w-5 h-5 text-orange-600" />
                  </div>
                  <div className="w-0.5 h-full bg-gray-200 mt-2" />
                </div>
                <div className="pb-6">
                  <p className="font-semibold text-gray-900 text-sm">Packing Your Order</p>
                  <p className="text-xs text-gray-500 mt-1">Your posters are carefully rolled in protective tubes</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 bg-gray-100 border-2 border-gray-300 rounded-full flex items-center justify-center">
                    <Truck className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">Ships in 2–3 Business Days</p>
                  <p className="text-xs text-gray-500 mt-1">You'll receive a tracking number once shipped</p>
                </div>
              </div>
            </div>
          </div>

          {/* Estimated delivery */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-6 mb-8 text-white" data-testid="delivery-estimate">
            <div className="flex items-center gap-3 mb-3">
              <Clock className="w-5 h-5 text-orange-400" />
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">Estimated Delivery</span>
            </div>
            <p className="text-lg font-bold">
              {(() => {
                const d1 = new Date(); d1.setDate(d1.getDate() + 5);
                const d2 = new Date(); d2.setDate(d2.getDate() + 8);
                const opts = { month: 'short', day: 'numeric' };
                return `${d1.toLocaleDateString('en-US', opts)} — ${d2.toLocaleDateString('en-US', opts)}`;
              })()}
            </p>
            <p className="text-sm text-gray-400 mt-1">Standard shipping via USPS / UPS</p>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/shop"
              className="flex-1 px-6 py-4 bg-gradient-to-r from-red-500 to-orange-500 text-white font-bold rounded-xl text-center hover:from-red-600 hover:to-orange-600 transition-all shadow-lg shadow-orange-500/20 flex items-center justify-center gap-2"
              data-testid="continue-shopping-btn">
              <ShoppingBag className="w-5 h-5" />
              Continue Shopping
            </Link>
            <Link to="/"
              className="flex-1 px-6 py-4 border-2 border-gray-200 text-gray-700 font-bold rounded-xl text-center hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
              data-testid="home-btn">
              Back to Home <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Footer note */}
          <p className="text-center text-xs text-gray-400 mt-10">
            A confirmation has been saved to your order. If you have any questions,{' '}
            contact us at <span className="font-medium">orders@posterfire.com</span>
          </p>
        </div>
      </div>
    );
  }

  // Failed / Pending state
  return (
    <div className="min-h-screen bg-white flex items-center justify-center" data-testid="order-failed">
      <div className="max-w-md mx-auto text-center px-4">
        <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-8">
          <AlertTriangle className="w-10 h-10 text-yellow-600" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-3">
          {status === 'pending' ? 'Payment Processing' : 'Something Went Wrong'}
        </h1>
        <p className="text-gray-500 mb-8">
          {status === 'pending'
            ? 'Your payment is still being processed. This usually resolves within a few minutes.'
            : 'We couldn\'t confirm your payment. Your card was not charged.'}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/cart"
            className="px-6 py-3 bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-700 transition-colors">
            Return to Cart
          </Link>
          <Link to="/shop"
            className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors">
            Browse Posters
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccessPage;
